<template>
  <div class="tw-my-12">
    <h2 class="tw-text-6xl tw-font-bold tw-text-center tw-mb-16">Tell us about your guests!</h2>

    <div class="lg:tw-w-3/4 xl:tw-w-3/5 tw-mx-auto">
      <div class="tw-mb-16 tw-mx-8" v-if="state.cateringOrder && state.cateringOrder.guest_count != 0">
        <div class="tw-text-right tw-text-lg tw-mr-3 tw-mb-4">
          <strong class="tw-font-black">{{ guestsAccountedFor }}/{{ state.cateringOrder.guest_count}}</strong> guests accounted for
        </div>
        <ProgressBar :percentage="percentageAccountedFor" />
      </div>

      <div :class="['tw-flex tw-justify-center tw-flex-wrap lg:tw-flex-nowrap', {'tw-mb-12 lg:tw-mb-24': isValid}, {'tw-mb-6 lg:tw-mb-12': !isValid}]" v-if="state.cateringOrder && state.cateringOrder.guest_count != 0">
        <GuestHungerItem
          hungerLevel="Light Eaters"
          hungerLevelDetails="lorem ipsum"
          :requiresAmount="props.cateringOrder.guest_count != 0"
          iconType="light"
          :amount="state.cateringOrder.light_eaters" 
          :max="state.cateringOrder.guest_count"
          @amountChanged="lightAmountChanged" />

        <GuestHungerItem
          hungerLevel="Typical Eaters"
          hungerLevelDetails="lorem ipsum"
          :requiresAmount="props.cateringOrder.guest_count != 0"
          iconType="typical"
          :amount="state.cateringOrder.regular_eaters"
          :max="state.cateringOrder.guest_count"
          @amountChanged="typicalAmountChanged" />
          
        <GuestHungerItem
          hungerLevel="Hungry Eaters"
          hungerLevelDetails="lorem ipsum"
          :requiresAmount="props.cateringOrder.guest_count != 0"
          iconType="hungry"
          :amount="state.cateringOrder.hungry_eaters"
          :max="state.cateringOrder.guest_count"
          @amountChanged="hungryAmountChanged" />
      </div>
    </div>

    <div class="tw-mb-6 lg:tw-mb-12 tw-mx-12 tw-flex tw-items-center tw-justify-center tw-text-red-500" v-if="state.cateringOrder && state.cateringOrder.guest_count != 0 && !isValid">
      <div>
        The provided guest counts do not match the amount of anticipated guests entered in the previous step: <strong class="tw-font-black" v-if="state.cateringOrder && state.cateringOrder!.guest_count"> {{state.cateringOrder!.guest_count}} guests</strong>
      </div>
    </div>

    <div :class="['tw-border-gray-300', {'tw-border-t tw-pt-16': state.cateringOrder && state.cateringOrder.guest_count != 0}]" v-if="state.cateringOrder">
      <div class="md:tw-w-3/4 lg:tw-w-3/5 tw-mx-auto tw-pb-8">
        <div>
          <h2 class="tw-text-4xl tw-font-bold tw-text-center tw-mb-6">Dietary Needs & Preferences</h2>
          <p class="tw-text-center tw-mb-12">Have someone in the group with a special dietary request? Let us know below and we'll make sure you've ordered something they can eat.</p>
        </div>
        <div 
          class="tw-mb-8 tw-rounded-3xl tw-overflow-hidden tw-cursor-pointer tw-shadow-lg tw-bg-white tw-px-16 tw-py-8 hover:tw-text-orange-medium tw-border tw-border-slate-100" 
          @click="allergensOpen = !allergensOpen">
          <div class="tw-relative tw-pr-24">
            <h2 class="tw-text-3xl tw-font-bold tw-mr-auto tw-transition-all tw-duration-300">
              Allergens to Avoid
              <span class="tw-font-normal tw-text-xl tw-pl-4 tw-italic tw-pt-4 ">
                {{ allergensOpen ? 'Click to hide' : 'Click to expand'}}
              </span>
            </h2>
            <span :class="['tw-absolute tw-top-1/2 tw--translate-y-1/2 tw-right-0 fa fa-chevron-right tw-transition tw-inline-block tw-origin-center tw-text-3xl', {'tw-rotate-90': allergensOpen}]"></span>
          </div>
        </div>
        <TwCollapse
          :duration="200"
          :collapseOpen="allergensOpen"
          class="tw-mx-10">
            <div class="tw-flex tw-flex-wrap">
              <TwCheckbox
                v-for="(allergy, allergy_sym) in allergens"
                :id="'allergy-' + allergy_sym"
                :key="'allergy-' + allergy_sym"
                :label="allergy"
                value="1"
                class="tw-mb-4 tw-w-1/2 lg:tw-w-1/5"
                :checked="state.cateringOrder!.allergens.indexOf(allergy_sym as string) > -1"
                @on-change="state.cateringOrder!.allergens.indexOf(allergy_sym as string) > -1 ? state.cateringOrder!.allergens.splice(state.cateringOrder!.allergens.indexOf(allergy_sym as string), 1) : state.cateringOrder!.allergens.push(allergy_sym as string)"
              />
            </div>
        </TwCollapse>
      </div>
      <div class="md:tw-w-3/4 lg:tw-w-3/5 tw-mx-auto tw-pb-8">
        <div 
          class="tw-mb-8 tw-rounded-3xl tw-overflow-hidden tw-cursor-pointer tw-shadow-lg tw-bg-white tw-px-16 tw-py-8 hover:tw-text-orange-medium tw-border tw-border-slate-100" 
          @click="restrictionsOpen = !restrictionsOpen">
          <div class="tw-relative tw-pr-24">
            <h2 class="tw-text-3xl tw-font-bold tw-mr-auto tw-transition-all tw-duration-300">
              Dietary Preferences
              <span class="tw-font-normal tw-text-xl tw-pl-4 tw-italic tw-pt-4 ">
                {{ restrictionsOpen ? 'Click to hide' : 'Click to expand'}}
              </span>
            </h2>
            <span :class="['tw-absolute tw-top-1/2 tw--translate-y-1/2 tw-right-0 fa fa-chevron-right tw-transition tw-inline-block tw-origin-center tw-text-3xl', {'tw-rotate-90': restrictionsOpen}]"></span>
          </div>
        </div>
        <TwCollapse
          :duration="200"
          :collapseOpen="restrictionsOpen"
          class="tw-mx-10">
            <div class="tw-flex tw-flex-wrap">
              <TwCheckbox
                v-for="(restriction, restriction_sym) in diets"
                :id="'restriction-' + restriction_sym"
                :key="'restriction-' + restriction_sym"
                :label="restriction"
                value="1"
                class="tw-mb-4 tw-w-1/2 lg:tw-w-1/5"
                :checked="state.cateringOrder!.diets.indexOf(restriction_sym as string) > -1"
                @on-change="state.cateringOrder!.diets.indexOf(restriction_sym as string) > -1 ? state.cateringOrder!.diets.splice(state.cateringOrder!.diets.indexOf(restriction_sym as string), 1) : state.cateringOrder!.diets.push(restriction_sym as string)"
              />
            </div>
        </TwCollapse>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import {
    reactive, computed, onMounted, ref
  } from 'vue';
  import type {PropType} from 'vue';
  import { watchEffect } from 'vue';

  // Data
  import type CateringOrder from '../../types/CateringOrder'

  import NutritionData from '../../classes/NutritionData'
  import ProgressBar from '../../components/ProgressBar.vue'
  import GuestHungerItem from './_GuestHungerItem.vue'
  import TwCheckbox from '../../components/TwCheckbox.vue'
  import TwCollapse from '../../components/TwCollapse.vue'

  const emit = defineEmits(['setCateringOrder']);

  const props = defineProps({
    cateringOrder: {
      type: (Object as PropType<CateringOrder>),
      default: null,
    }
  });

  const state = reactive({
    cateringOrder: null as (CateringOrder | null)
  })

  const allergens = NutritionData.allergens
  const diets = NutritionData.diets
  const allergensOpen = ref(true);
  const restrictionsOpen = ref(true);


  onMounted(() => {
    state.cateringOrder = props.cateringOrder;
    if (state.cateringOrder && state.cateringOrder.guest_count > 0) {
      if ((state.cateringOrder.light_eaters + state.cateringOrder.regular_eaters + state.cateringOrder.hungry_eaters) == 0) {
        state.cateringOrder.regular_eaters = state.cateringOrder.guest_count;
      }
    }
  })

  watchEffect(() => {
    const cateringOrder = state.cateringOrder;
    if (cateringOrder === null) return;
    emit('setCateringOrder', cateringOrder);
  })

  const guestsAccountedFor = computed(() => {
    if (state.cateringOrder) {
      return (state.cateringOrder.light_eaters + state.cateringOrder.regular_eaters + state.cateringOrder.hungry_eaters);
    } else {
      return 0
    }
  })

  const percentageAccountedFor = computed(() => {
    if (state.cateringOrder && state.cateringOrder!.guest_count && guestsAccountedFor.value > 0 && state.cateringOrder.guest_count > 0) {
      return Math.min(100, ((guestsAccountedFor.value/state.cateringOrder!.guest_count) * 100))
    } else {
      return 0;
    }
  })

  const isValid = computed(() => {
    if (state.cateringOrder && state.cateringOrder!.guest_count && guestsAccountedFor.value == state.cateringOrder.guest_count) {
      return true
    } else {
      return false
    }
  })

  const normalizeCount = (value: number | string | null | undefined) => {
    const asNumber = Number(value);
    return Number.isFinite(asNumber) ? asNumber : 0;
  }

  const syncTypicalEaters = () => {
    if (!state.cateringOrder) {
      return;
    }

    const guestCount = normalizeCount(state.cateringOrder.guest_count);
    const light = normalizeCount(state.cateringOrder.light_eaters);
    const hungry = normalizeCount(state.cateringOrder.hungry_eaters);
    const remainder = guestCount - (light + hungry);

    state.cateringOrder.regular_eaters = Math.max(0, remainder);
  }

  const lightAmountChanged = (amount: number) => {
    if (!state.cateringOrder) {
      return;
    }

    state.cateringOrder.light_eaters = normalizeCount(amount);
    syncTypicalEaters();
  }

  const typicalAmountChanged = (amount: number) => {
    if (!state.cateringOrder) {
      return;
    }

    state.cateringOrder.regular_eaters = normalizeCount(amount);
  }

  const hungryAmountChanged = (amount: number) => {
    if (!state.cateringOrder) {
      return;
    }

    state.cateringOrder.hungry_eaters = normalizeCount(amount);
    syncTypicalEaters();
  }

</script>
